import { useSuspenseQuery } from '@apollo/client'
import Intercom from '@intercom/messenger-js-sdk'
import * as Sentry from '@sentry/react'
import isNumber from 'lodash/isNumber'
import { useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router'
import { LayoutSkeleton } from '@/components/layout/layout-skeleton'
import { USER_COMPANIES_QUERY } from '@/components/root/gql'
import { SELECTED_COMPANY_KEY } from '@/config/local-storage'
import { NoAccessError } from '@/lib/errors'
import { useIdCompany } from '@/lib/hooks'
import type {
  UserCompaniesQuery,
  UserCompaniesQueryVariables,
} from '@/components/root/gql.types.gen'

export const CompanySetter = ({ children }: { children: React.ReactNode }) => {
  const { idCompany } = useIdCompany()
  const { data } = useSuspenseQuery<
    UserCompaniesQuery,
    UserCompaniesQueryVariables
  >(USER_COMPANIES_QUERY, {
    variables: { idCompany: idCompany || undefined },
  })
  const [, setSearchParams] = useSearchParams()

  const user = useMemo(() => data?.user ?? null, [data])
  const companies = useMemo(() => data?.user?.companies ?? [], [data])
  const selectedCompany = useMemo(
    () => companies.find((c) => c.id === idCompany) ?? null,
    [companies, idCompany]
  )
  const isSuperadmin = useMemo(() => {
    return user?.rolesInCompany.includes('kinsta-superadmin') ?? false
  }, [user])

  useEffect(() => {
    Intercom({
      app_id: 'gwgnqiw6',
      intercom_user_jwt: user?.sevallaIntercomJwt,
      name: user?.fullName ?? '',
      created_at: isNumber(user?.createdAt)
        ? Math.round(user.createdAt / 1000)
        : undefined,
      company: selectedCompany
        ? {
            company_id: selectedCompany.id,
            created_at: isNumber(selectedCompany?.createdAt)
              ? Math.round(selectedCompany.createdAt / 1000)
              : undefined,
            name: selectedCompany.name,
          }
        : null,
      companies: (companies.map((c) => ({
        company_id: c.id,
        created_at: isNumber(c?.createdAt)
          ? Math.round(c.createdAt / 1000)
          : undefined,
        name: c.name,
      })) ?? []) as [any],
    })
  }, [
    companies,
    selectedCompany,
    user?.createdAt,
    user?.fullName,
    user?.sevallaIntercomJwt,
  ])

  useEffect(() => {
    if (user?.id) {
      Sentry.setUser({
        name: user.fullName ?? '',
        email: user.email ?? '',
        id: user.id ?? '',
      })
      Sentry.setTag('idCompany', idCompany)
    }
  }, [user?.email, user?.fullName, user?.id, idCompany])

  useEffect(() => {
    if (idCompany) {
      if (isSuperadmin) {
        localStorage.setItem(SELECTED_COMPANY_KEY, idCompany)
        return
      }

      const company = companies.find((c) => c.id === idCompany)

      if (!company) {
        throw new NoAccessError()
      }

      localStorage.setItem(SELECTED_COMPANY_KEY, idCompany)
    } else if (companies.length > 0) {
      const savedIdCompany = localStorage.getItem(SELECTED_COMPANY_KEY)
      const savedCompany = savedIdCompany
        ? companies.find((c) => c.id === savedIdCompany)
        : null
      const company = savedCompany ?? companies[0]
      setSearchParams({ idCompany: company.id }, { replace: true })
    }
  }, [idCompany, companies, setSearchParams, isSuperadmin])

  const isCompanyRedirectPending = !idCompany && companies.length > 0

  if (isCompanyRedirectPending) {
    return <LayoutSkeleton />
  }

  return <>{children}</>
}
